import * as THREE from 'three';
import Command from './Command';
import CommandIdEnum from '../enums/CommandIdEnums';
import CommandStack from '../logic/CommandStack';
import { useCameraControlsStore } from '../store/cameraControlsStore';
import { useModelPropertiesStore } from '../store/modelPropertiesStore/modelPropertiesStore';
import { sceneUnitsFromMm } from '../utils/scaler';
import { ElementTypeEnum } from '../elementTypeMappings';
import { isProductMeshUserData } from '../interfaces/ProductMeshUserData';
import { getParentThreeProductByType } from '../utils/productUserDataUtils';
import { ModelManager } from '../model/ModelManager';

export default abstract class FocusOnCmd extends Command {

    // Roughly a second at 60fps - long enough to cover the race where the target's document
    // hasn't been staged into the scene graph yet, short enough to fail fast otherwise.
    static readonly #MAX_PARENT_WAIT_FRAMES = 60;

    readonly #screenCoveragePercent: number;

    constructor(id: CommandIdEnum, commandStack: CommandStack, screenCoveragePercent: number) {
        super(id, commandStack);
        this.#screenCoveragePercent = screenCoveragePercent;
    }

    protected abstract findTargetObject(): THREE.Object3D | undefined;

    /**
     * Walks up the parent tree of `target` and returns the first ancestor that is a
     * building storey, wrapped in an array.  Returns an empty array when no storey
     * ancestor is found (e.g. the target is a free-standing scene object).
     */
    protected getVisibleStoreys(target: THREE.Object3D): THREE.Object3D[] {
        const storey = getParentThreeProductByType(target, ElementTypeEnum.Storey);
        return storey ? [storey] : [];
    }

    /**
     * Computes the per-side padding needed so the object fills `screenCoveragePercent`
     * of the 2D view area.
     *
     * Derivation:
     *   linear_coverage = sqrt(screenCoveragePercent / 100)
     *   padding = maxDim * (1 - linear_coverage) / (2 * linear_coverage)
     */
    #computePadding(maxDim: number): number {
        const linearCoverage = Math.sqrt(this.#screenCoveragePercent / 100);
        return maxDim * (1 - linearCoverage) / (2 * linearCoverage);
    }

    start(): void {
        super.start();
        this.#focusWhenReady(0);
    }

    // The scene object this command targets is parented into the THREE scene graph by a
    // component mounted inside <Canvas> (its own react-three-fiber reconciler root), while this
    // command is typically triggered from the outer DOM React tree - there's no ordering
    // guarantee between the two, so this command can run before the target has been added to
    // the scene at all (target.parent === null). A scene object without a BIM-product parent
    // still ends up under SceneManager.scene via SceneGeometry's fallback, so null specifically
    // means "not staged yet", not "intentionally root-level" - retry a few frames to let staging
    // catch up instead of computing a bounding box from an unparented, misplaced object.
    #focusWhenReady(attempt: number): void {
        const target = this.findTargetObject();
        if (!target) return;

        if (target.parent === null) {
            if (attempt >= FocusOnCmd.#MAX_PARENT_WAIT_FRAMES) {
                console.error(`${this.constructor.name}: target was never added to the scene graph`);
                return;
            }
            requestAnimationFrame(() => this.#focusWhenReady(attempt + 1));
            return;
        }

        this.#focus(target);
    }

    #focus(target: THREE.Object3D): void {
        const visibleStoreys = this.getVisibleStoreys(target);
        if (visibleStoreys.length > 0) {
            ModelManager.getInstance().modelGroup.traverse((obj) => {
                if (isProductMeshUserData(obj.userData) &&
                    obj.userData.product.productData.productType === ElementTypeEnum.Storey) {
                    obj.visible = visibleStoreys.includes(obj);
                }
            });
        }

        // Box3.setFromObject only refreshes target's own matrix, not its ancestors' - if focus
        // fires in the same tick the target was (re)parented, a stale ancestor matrixWorld could
        // still yield a wrong bbox even though parenting itself is correct.
        target.parent?.updateWorldMatrix(true, false);

        const bbox = new THREE.Box3().setFromObject(target);
        if (bbox.isEmpty()) {
            console.error(`${this.constructor.name}: bounding box is empty`);
            return;
        }

        const controls = useCameraControlsStore.getState().controls;
        if (!controls) {
            console.error(`${this.constructor.name}: camera controls not available`);
            return;
        }

        const size = new THREE.Vector3();
        bbox.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z);
        const padding = this.#computePadding(maxDim);

        // Reset to a known top-down orientation first (no animation), matching the
        // SceneDirector pattern so that rotate(rotationOffset) below starts from azimuth=0.
        const center = bbox.getCenter(new THREE.Vector3());
        controls.setLookAt(
            center.x, center.y, center.z + sceneUnitsFromMm(10000),
            center.x, center.y, center.z,
            false
        );

        controls.fitToBox(bbox, false, {
            paddingLeft: padding,
            paddingRight: padding,
            paddingTop: padding,
            paddingBottom: padding,
        });

        const rotationOffset = useModelPropertiesStore.getState().rotationOffset;
        if (rotationOffset) {
            controls.rotate(rotationOffset, 0, false);
        }
    }
}
